/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * useVerifiedPhone — drives the Friends-feature phone gate. Subscribes to
 * /users/{uid}/phoneNumber for the signed-in user and reports:
 *   - verified: true once the field exists and is a non-empty E.164 string.
 *   - loading: true while the first onValue snapshot is in flight.
 *
 * Returning {verified: false, loading: true} on cold boot would flash the
 * sheet for a frame on every Friends-tab open even for users who have
 * already verified, so callers must check `loading` before rendering the
 * gate.
 */

import { useEffect, useState } from "react";
import { getDatabase, onValue, ref } from "firebase/database";
import { useAuth } from "@/contexts/AuthContext";

export interface UseVerifiedPhoneReturn {
  verified: boolean;
  loading: boolean;
}

export function useVerifiedPhone(): UseVerifiedPhoneReturn {
  const { user } = useAuth();
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setVerified(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const db = getDatabase();
    const unsub = onValue(
      ref(db, `users/${user.uid}/phoneNumber`),
      (snap) => {
        const value = snap.val();
        setVerified(typeof value === "string" && value.startsWith("+"));
        setLoading(false);
      },
      (err) => {
        // Permission errors should never happen for a user reading their own
        // phoneNumber, but if they do, treat as unverified and stop loading
        // so the gate can show. The sheet's own write path will surface a
        // real error if rules are misconfigured.
        console.warn("[useVerifiedPhone] read failed:", err.message);
        setVerified(false);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [user]);

  return { verified, loading };
}
